import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth, useLogout } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';

const mainNav = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: (
      <svg className="w-4.5 h-4.5 w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    path: '/weekly-plan',
    label: 'Weekly Plan',
    icon: (
      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
];

const toolsNav = [
  {
    path: '/sitewide-tasks',
    label: 'Sitewide Fixes',
    icon: (
      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    path: '/page-tasks',
    label: 'Page Tasks',
    icon: (
      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    path: '/export',
    label: 'CSV Export',
    icon: (
      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    ),
  },
  {
    path: '/deleted-tasks',
    label: 'Deleted Tasks',
    icon: (
      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0V5a2 2 0 012-2h4a2 2 0 012 2v2" />
      </svg>
    ),
  },
  {
    path: '/notes',
    label: 'My Notes',
    icon: (
      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: (
      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

function NavItem({ item }: { item: typeof mainNav[0] }) {
  return (
    <li>
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all duration-150 cursor-pointer relative rounded-lg mx-2',
            isActive
              ? 'text-white bg-[#27272A] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-[3px] before:bg-[#F97316] before:rounded-r-full'
              : 'text-[#71717A] hover:text-white hover:bg-[#27272A]'
          )
        }
      >
        {item.icon}
        {item.label}
      </NavLink>
    </li>
  );
}

export default function Sidebar() {
  const { user } = useAuth();
  const logout = useLogout();
  const isAdmin = (user as any)?.role !== 'member';

  return (
    <aside
      className="flex-shrink-0 flex flex-col h-full"
      style={{ width: 260, backgroundColor: '#18181B' }}
    >
      {/* Logo */}
      <div className="px-5 py-4 border-b border-[#27272A]">
        <div className="flex items-center gap-3">
          <img src="/tkxel-logo.svg" alt="Tkxel" className="h-8 w-auto flex-shrink-0" />
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#F97316' }}>
            SEO Dashboard
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* MAIN section */}
        <div className="mb-4">
          <p className="px-6 mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#3F3F46]">
            Main
          </p>
          <ul className="space-y-0.5">
            {mainNav.map(item => <NavItem key={item.path} item={item} />)}
          </ul>
        </div>

        {/* TOOLS section */}
        <div>
          <p className="px-6 mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#3F3F46]">
            Tools
          </p>
          <ul className="space-y-0.5">
            {toolsNav.filter(item => !['/notes', '/settings', '/deleted-tasks'].includes(item.path) || isAdmin).map(item => <NavItem key={item.path} item={item} />)}
          </ul>
        </div>
      </nav>

      {/* User section */}
      <div className="px-4 py-4 border-t border-[#27272A]">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ backgroundColor: '#F97316' }}
          >
            {user?.username?.charAt(0).toUpperCase() ?? 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">
              {(user as any)?.display_name || user?.username || 'User'}
            </p>
            <p className="text-[#52525B] text-xs capitalize">
              {(user as any)?.role === 'member' ? 'Team Member' : 'Administrator'}
            </p>
          </div>
          <button
            onClick={() => logout.mutate()}
            className="p-1.5 text-[#52525B] hover:text-white hover:bg-[#27272A] rounded-lg transition-all duration-150 flex-shrink-0"
            title="Sign out"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}
